import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import type { DetectionStats, EndpointRisk, TimeSeriesPoint, OwaspCategory, ObservabilityStats, DashboardOverview } from '@/types/api';

// ── Query key factory ──────────────────────────────────────────────────────
export const analyticsKeys = {
  dashboard: ['analytics', 'dashboard'] as const,
  timeline: (hours: number) => ['analytics', 'timeline', hours] as const,
  detectionStats: ['analytics', 'detection-stats'] as const,
  endpointRisk: ['analytics', 'endpoint-risk'] as const,
  owaspStats: ['analytics', 'owasp-stats'] as const,
  observability: ['analytics', 'observability'] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useDashboard() {
  return useQuery({
    queryKey: analyticsKeys.dashboard,
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardOverview>('/analytics/dashboard');
      return data;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useActivityTimeline(hours: number = 24) {
  return useQuery({
    queryKey: analyticsKeys.timeline(hours),
    queryFn: async () => {
      const { data } = await apiClient.get<TimeSeriesPoint[]>(
        '/analytics/activity-timeline',
        { params: { hours } },
      );
      return data;
    },
    staleTime: 60_000, // Timeline refreshes every minute
  });
}

export function useDetectionStats() {
  return useQuery({
    queryKey: analyticsKeys.detectionStats,
    queryFn: async () => {
      const { data } = await apiClient.get<DetectionStats>('/analytics/detection-stats');
      return data;
    },
    staleTime: 30_000,
  });
}

export function useEndpointRisk(limit: number = 10) {
  return useQuery({
    queryKey: analyticsKeys.endpointRisk,
    queryFn: async () => {
      const { data } = await apiClient.get<EndpointRisk[]>(
        '/analytics/endpoint-risk',
        { params: { limit } },
      );
      return data;
    },
  });
}

export interface TopDetector {
  detector: string;
  count: number;
}

export function useTopDetectors(limit = 10) {
  return useQuery({
    queryKey: ['analytics', 'top-detectors', limit] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<TopDetector[]>('/analytics/top-detectors', { params: { limit } });
      return data;
    },
    staleTime: 60_000,
  });
}

export function useOwaspStats() {
  return useQuery({
    queryKey: analyticsKeys.owaspStats,
    queryFn: async () => {
      const { data } = await apiClient.get<OwaspCategory[]>('/analytics/owasp-stats');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useObservabilityStats() {
  return useQuery({
    queryKey: analyticsKeys.observability,
    queryFn: async () => {
      const { data } = await apiClient.get<ObservabilityStats>('/analytics/observability');
      return data;
    },
    staleTime: 30_000,
  });
}

// ── OWASP & MITRE ──────────────────────────────────────────────────────────

export type OwaspMitreRange = '24h' | '7d' | '30d';

export interface OwaspMitreCategoryStats {
  code: string;
  framework: 'llm' | 'atlas';
  critical: number;
  high: number;
  medium: number;
  low: number;
  lastSeen: string | null;
  recentAlerts: { id: string; title: string; severity: string; endpointHostname: string; createdAt: string }[];
}

export interface OwaspMitreStats {
  summary: { total: number; critical: number; high: number; medium: number; low: number; mitigated: number; riskScore: number };
  timeline: { date: string; critical: number; high: number; medium: number; low: number }[];
  categories: OwaspMitreCategoryStats[];
}

export function useOwaspMitreStats(range: OwaspMitreRange = '7d') {
  return useQuery({
    queryKey: ['analytics', 'owasp-mitre', range] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<OwaspMitreStats>('/analytics/owasp-mitre', { params: { range } });
      return data;
    },
    staleTime: 60_000,
  });
}

// ── Org risk score ──────────────────────────────────────────────────────────

export interface OrgRiskScore {
  score: number;
  band: 'low' | 'moderate' | 'elevated';
  endpointCount: number;
}

export function useOrgRiskScore() {
  return useQuery({
    queryKey: ['analytics', 'org-risk-score'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<OrgRiskScore>('/analytics/org-risk-score');
      return data;
    },
    staleTime: 60_000,
    // Offset from the other Dashboard widgets' 60s polls so refetches don't all land on the same tick.
    refetchInterval: 63_000,
  });
}

// ── Sensitive data flow ─────────────────────────────────────────────────────

export type SensitiveDataType = 'pii' | 'secrets' | 'source_code';

export interface SensitiveDataFlow {
  totalSessions: number;
  dataTypes: { type: SensitiveDataType; label: string; sessionCount: number }[];
  vendors: { vendor: string; sessionCount: number }[];
  flows: { dataType: SensitiveDataType; vendor: string; sessionCount: number }[];
}

export function useSensitiveDataFlow() {
  return useQuery({
    queryKey: ['analytics', 'sensitive-data-flow'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<SensitiveDataFlow>('/analytics/sensitive-data-flow');
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 67_000,
  });
}

// ── Vendor exposure ─────────────────────────────────────────────────────────

export interface VendorExposure {
  vendor: string;
  sessionCount: number;
  percentage: number;
  models: { model: string; tool: string; sessionCount: number }[];
}

export interface VendorExposureStats {
  totalSessions: number;
  vendorCount: number;
  vendors: VendorExposure[];
}

export function useVendorExposure() {
  return useQuery({
    queryKey: ['analytics', 'vendor-exposure'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<VendorExposureStats>('/analytics/vendor-exposure');
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 71_000,
  });
}

// ── Token usage by agent ─────────────────────────────────────────────────────

export interface TokenUsageByAgent {
  agent: string;
  label: string;
  totalTokens: number;
  percentage: number;
}

export interface TokenUsageStats {
  totalTokens: number;
  agents: TokenUsageByAgent[];
}

export function useTokenUsageByAgent() {
  return useQuery({
    queryKey: ['analytics', 'token-usage-by-agent'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<TokenUsageStats>('/analytics/token-usage-by-agent');
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 74_000,
  });
}

// ── Attention queue ─────────────────────────────────────────────────────────

export interface AttentionQueueItem {
  id: string;
  source: 'alert' | 'detection';
  title: string;
  severity: string;
  verdict: string | null;
  detector: string | null;
  endpointHostname: string;
  createdAt: string;
}

export function useAttentionQueue(limit: number = 10) {
  return useQuery({
    queryKey: ['analytics', 'attention-queue', limit] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<AttentionQueueItem[]>('/analytics/attention-queue', { params: { limit } });
      return data;
    },
    staleTime: 20_000,
    // Not a clean multiple of the other widgets' ~60-70s intervals, so its polls drift out of sync with theirs.
    refetchInterval: 34_000,
  });
}
